import { z } from "zod";
import { dateRangeValidation } from "../utils/validationUtils";

export const creativeCreateSchema = z
  .object({
    file: z.any().refine((files) => {
      if (!files || files.length === 0) return false;
      return true;
    }, "File is required"),
    adGroupId: z.string().min(1, "Ad Group is required"),
    // Optional: house and filler content legitimately has no owner. "" is the
    // Unassigned choice, which the backend stores as NULL.
    brandId: z.string().optional(),
    description: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  })
  .refine(...dateRangeValidation.create.startDateNotInPast())
  .refine(...dateRangeValidation.create.endDateNotBeforeStartDate());

const creativeUpdateFields = z.object({
  file: z.any().optional(),
  // Required here as it is on create: the edit form always opens with the
  // creative's current ad group selected, so the only way to submit an empty
  // one is to deliberately clear it — and an unassigned creative reaches no
  // playlist. The mutation payload stays a Partial, since the page sends only
  // changed fields.
  adGroupId: z.string().min(1, "Ad Group is required"),
  // Optional, and clearable: selecting Unassigned sends "", which the backend
  // maps to NULL — that is how a creative is taken away from a brand.
  brandId: z.string().optional(),
  description: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

/**
 * The edit-creative schema, seeded with the start date the form opened with.
 *
 * A factory rather than a constant because the start-date rule is relative: a
 * creative that is already running carries a past start date, and that stored
 * value has to stay acceptable, while any *new* date the operator picks must be
 * today or later. Pass the same `formatDateForInput(creative.startDate)` string
 * the form uses as its default value.
 */
export const buildCreativeUpdateSchema = (originalStartDate?: string) =>
  creativeUpdateFields
    .refine(
      ...dateRangeValidation.update.startDateNotInPastIfChanged(
        originalStartDate
      )
    )
    .refine(...dateRangeValidation.update.endDateNotBeforeStartDate());

export const creativeFilterSchema = z.object({
  adGroupId: z.string().optional(),
  brandId: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
});

/** Derived from the creative's flight window — never stored on the row. */
export type CreativeStatus = "ACTIVE" | "UPCOMING" | "EXPIRED";

export type CreativeFormData = z.infer<typeof creativeCreateSchema>;
export type CreativeUpdateFormData = z.infer<typeof creativeUpdateFields>;
export type CreativeFilterFormData = z.infer<typeof creativeFilterSchema>;

export interface Media {
  id: string;
  creativeId: string;
  fileName: string;
  fileUrl: string;
  format: string;
  thumbnailUrl?: string;
  thumbnailPresignedUrl?: string;
  resolution?: string;
  frameRate?: string;
  duration?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Creative {
  id: string;
  uniqueId: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  adGroupId?: string;
  /** Owning brand (a Brand-role user), or undefined when unassigned. */
  brandId?: string;
  brandName?: string;
  tenantId?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  adGroup?: {
    id: string;
    name: string;
  };
  media: Media[];
}

export interface CreativesResponse {
  creatives: Creative[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface CreativeUsagePlaylist {
  id: string;
  name: string;
  /** Null for playlists that carry no type — only media-house ones do. */
  type: string | null;
}

/**
 * How far deleting a creative would reach. Deleting removes the creative from
 * every playlist that holds it, so the confirmation has to say how many that
 * is before the operator commits.
 */
export interface CreativeUsage {
  /** Distinct playlists the selected creatives sit in. */
  playlistCount: number;
  /** Distinct screens currently playing any of them. */
  screenCount: number;
  playlists: CreativeUsagePlaylist[];
}

export interface CreativeFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  type?: string;
  adGroupId?: string;
  /** Brand owner filter. The literal "unassigned" matches creatives with no brand. */
  brandId?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

export interface UpdateCreativePayload {
  id: string;
  /**
   * Partial because the Creatives page diffs the form against the row and
   * sends only what changed — a rename must not have to re-post the ad group.
   */
  creative: Partial<CreativeUpdateFormData>;
}
